import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Direction = 'ltr' | 'rtl';
type Language = 'en' | 'fa' | 'ar';

interface DirectionContextType {
  direction: Direction;
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const DirectionContext = createContext<DirectionContextType>({
  direction: 'ltr',
  language: 'en',
  setLanguage: () => {},
  t: (key) => key,
});

const RTL_LANGUAGES: Language[] = ['fa', 'ar'];

// Basic translations — extend as needed
const TRANSLATIONS: Record<Language, Record<string, string>> = {
  en: {},
  fa: {
    'Dashboard': 'داشبورد',
    'Customers': 'مشتریان',
    'Invoices': 'فاکتورها',
    'Settings': 'تنظیمات',
    'Home': 'خانه',
    'More': 'بیشتر',
    'Search': 'جستجو',
    'Filter': 'فیلتر',
    'Save': 'ذخیره',
    'Cancel': 'انصراف',
    'Delete': 'حذف',
    'Edit': 'ویرایش',
    'New': 'جدید',
    'Total': 'مجموع',
    'Active': 'فعال',
    'Inactive': 'غیرفعال',
    'Paid': 'پرداخت شده',
    'Pending': 'در انتظار',
    'Overdue': 'عقب‌افتاده',
    'Draft': 'پیش‌نویس',
    'Amount': 'مبلغ',
    'Date': 'تاریخ',
    'Status': 'وضعیت',
    'Name': 'نام',
    'Email': 'ایمیل',
    'Phone': 'تلفن',
    'Load More': 'بارگذاری بیشتر',
    'No results found': 'نتیجه‌ای یافت نشد',
    'Sign In': 'ورود',
    'Sign Out': 'خروج',
    'Profile': 'پروفایل',
    'Help & Support': 'راهنما و پشتیبانی',
    'Notifications': 'اعلان‌ها',
    'General': 'عمومی',
    'Company': 'شرکت',
    'Users': 'کاربران',
    'System': 'سیستم',
    'Backup': 'پشتیبان‌گیری',
    'Vendors': 'تأمین‌کنندگان',
    'Expenses': 'هزینه‌ها',
    'Reports': 'گزارش‌ها',
    'Accounts': 'حساب‌ها',
  },
  ar: {
    'Dashboard': 'لوحة القيادة',
    'Customers': 'العملاء',
    'Invoices': 'الفواتير',
    'Settings': 'الإعدادات',
    'Home': 'الرئيسية',
    'More': 'المزيد',
    'Search': 'بحث',
    'Filter': 'تصفية',
    'Save': 'حفظ',
    'Cancel': 'إلغاء',
    'Delete': 'حذف',
    'Edit': 'تعديل',
    'New': 'جديد',
    'Total': 'المجموع',
    'Active': 'نشط',
    'Paid': 'مدفوع',
    'Pending': 'قيد الانتظار',
    'Overdue': 'متأخر',
    'Draft': 'مسودة',
    'Amount': 'المبلغ',
    'Date': 'التاريخ',
    'Status': 'الحالة',
    'Name': 'الاسم',
    'Sign In': 'تسجيل الدخول',
    'Sign Out': 'تسجيل الخروج',
  },
};

export function DirectionProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    return (localStorage.getItem('app-language') as Language) || 'en';
  });

  const direction: Direction = RTL_LANGUAGES.includes(language) ? 'rtl' : 'ltr';

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('app-language', lang);
  };

  // Apply direction to document
  useEffect(() => {
    // RTL layout disabled for now — only language/font changes
    // Full RTL requires refactoring all positional CSS to logical properties
    document.documentElement.lang = language;
    if (RTL_LANGUAGES.includes(language)) {
      document.documentElement.style.fontFamily = '"Vazirmatn", "Inter", ui-sans-serif, system-ui, sans-serif';
    } else {
      document.documentElement.style.fontFamily = '';
    }
  }, [direction, language]);

  const t = (key: string): string => {
    if (language === 'en') return key;
    return TRANSLATIONS[language]?.[key] || key;
  };

  return (
    <DirectionContext.Provider value={{ direction, language, setLanguage, t }}>
      {children}
    </DirectionContext.Provider>
  );
}

export function useDirection() {
  return useContext(DirectionContext);
}
